/* Copyright (c) 2020-present CloudIO Inc */

import {
  FunctionsScheduledRunsStatus,
  IoDatasourceAttributes,
  IoFunctions,
  LiveStatus,
  ScheduleUpdate,
} from '@cloudio-saas/datasource-types';
import { atom, selector } from '@cloudio-saas/statex';
import { observable } from '@legendapp/state';
import {
  AnyItem,
  Chartjs,
  DataStore,
  DataStoreReference,
  DataGrid,
  Select,
  SidebarDraggableList,
  SmartSearchAttribute,
  AsyncSelect,
  LOVField,
  BreadcrumbNavigation,
  Area,
} from 'componentregistry';
import { contextAppIdAtom, developerModeAtom } from 'designer/atoms';
import { windowSizeAtom } from 'hooks/useWindowSize';
import { isDeveloperSelector, isViewOnlySelector } from 'root/useUser';
import { routeAtom } from 'routes/RouteState';

export function getDataStoreAlias(item: AnyItem): string | undefined {
  switch (item.type) {
    case 'Chartjs':
      return (item as Chartjs.ChartjsItem).props.dsalias;
    case 'Select':
      return (item as Select.SelectItem).props.comboDatasourceAlias;
    case 'LOVField':
      return (item as LOVField.LOVFieldItem).props.comboDatasourceAlias;
    case 'LOVColumn':
      return (item as DataGrid.LOVColumn.LOVColumnItem).props
        .comboDatasourceAlias;
    case 'AsyncSelect':
      return (item as AsyncSelect.AsyncSelectItem).props.comboDatasourceAlias;
    case 'SelectColumn':
      return (item as DataGrid.SelectColumn.SelectColumnItem).props
        .comboDatasourceAlias;
    case 'AsyncSelectColumn':
      return (item as DataGrid.AsyncSelectColumn.AsyncSelectColumnItem).props
        .comboDatasourceAlias;
    case 'DataStoreReference':
      return (item as DataStoreReference.DataStoreReferenceItem).props.alias;
    case 'DataStore':
      return (item as DataStore.DataStoreItem).props.alias;
    case 'SidebarDraggableList':
      return (item as SidebarDraggableList.SidebarDraggableListItem).props
        .alias;
    case 'SmartSearchAttribute':
      return (item as SmartSearchAttribute.SmartSearchAttributeItem).props
        .dsAlias;
    case 'BreadcrumbNavigation':
      return (item as BreadcrumbNavigation.BreadcrumbNavigationItem).props
        .comboDatasourceAlias;
    case 'Area':
      return (item as Area.AreaItem).props.alias;
    default:
      return '';
  }
}

export const contextMenuAtom = atom({
  path: ['transient', 'sidebar', 'contextMenu'],
  defaultValue: {
    top: 0,
    left: 0,
    appUid: '',
    pageId: '',
    itemId: '',
    orphan: false,
    dsAlias: '',
  },
});

export const runtimeContextMenuAtom = atom({
  path: ['transient', 'sidebar', 'runtimeContextMenu'],
  defaultValue: {
    top: 0,
    left: 0,
    pageId: '',
    itemId: '',
    orphan: false,
    dsAlias: '',
    rowIndex: -1,
  },
});

export type SidebarPanelName =
  | 'HIDDEN'
  | 'Launch'
  | 'agentGroups'
  | 'api'
  | 'appSettings'
  | 'apps'
  | 'auth'
  | 'clearCache'
  | 'componentStore'
  | 'components'
  | 'connections'
  | 'dbScripts'
  | 'uiScripts'
  | 'ds'
  | 'emails'
  | 'extds'
  | 'functions'
  | 'images'
  | 'kafka'
  | 'languages'
  | 'live'
  | 'lookups'
  | 'more'
  | 'pages'
  | 'patches'
  | 'profiles'
  | 'props'
  | 'roles'
  | 'jobs'
  | 'settings'
  | 'sql'
  | 'storage'
  | 'scheduledRuns'
  | 'testScripts'
  | 'tree'
  | 'users'
  | 'wf'
  | 'wfNodes'
  | 'exports'
  | 'users-dashboard'
  | 'profileView'
  | 'application-dashboard'
  | 'appGroup'
  | 'validate-scripts-dashboard'
  | 'agentic-wf';

export const activeSidebarPanelAtom = atom<SidebarPanelName>({
  path: ['transient', 'sidebar', 'panel', 'active'],
  defaultValue: 'tree',
  urlParam: true,
});

export const activeSidebarPanelSelector = selector<SidebarPanelName>({
  path: ['transient', 'sidebar', 'panel', ':appUid', 'activeSelector'],
  get: ({ get, params }) => {
    const appUid = params.appUid;
    if (!appUid) {
      throw new Error(
        'appUid is missing from params in activeSidebarPanelSelector!',
      );
    }
    const wfPage = get([...routeAtom.path, 'page']) === 'wf';
    const agenticWfPage = get([...routeAtom.path, 'page']) === 'agentic-wf';
    const developer = get(isDeveloperSelector, { params });
    const readOnlyDev = get(isViewOnlySelector, { params });
    let activeSidebarPanel = get(activeSidebarPanelAtom);
    const devMode = get(developerModeAtom, { params });
    if (
      (wfPage || agenticWfPage || (!developer && !readOnlyDev) || !devMode) &&
      ['tree', 'props'].includes(activeSidebarPanel)
    ) {
      activeSidebarPanel = 'HIDDEN';
    }
    return activeSidebarPanel;
  },
});

export const activePinnedSidebarPanelAtom = atom<SidebarPanelName>({
  path: ['db', 'IoUserSettings', ':userName', 'md', 'pinnedPanel'],
  defaultValue: 'props',
});

export const activePinnedSidebarPanelSelector = selector<SidebarPanelName>({
  path: ['transient', 'IoUserSettings', ':userName', 'pinnedPanel'],
  get: ({ get, params }) => {
    const { width } = get(windowSizeAtom);
    if (width <= 1200) {
      return 'HIDDEN';
    }
    if (get([...routeAtom.path, 'page']) === 'wf') {
      return 'HIDDEN';
    }
    if (get([...routeAtom.path, 'page']) === 'agentic-wf') {
      return 'HIDDEN';
    }
    let pannel = get(activePinnedSidebarPanelAtom, {
      params,
    });
    const wfPage = get([...routeAtom.path, 'page']) === 'wf';
    const agenticWfPage = get([...routeAtom.path, 'page']) === 'agentic-wf';
    const appUid = get(contextAppIdAtom);
    const developer = get(isDeveloperSelector, { params: { appUid } });
    const readOnlyDev = get(isViewOnlySelector, { params: { appUid } });
    const devMode = get(developerModeAtom, { params: { appUid } });
    if (
      (wfPage || agenticWfPage || (!developer && !readOnlyDev) || !devMode) &&
      ['tree', 'props', 'components'].includes(pannel)
    ) {
      pannel = 'HIDDEN';
    }
    return pannel;
  },
  set: ({ set, params }, value) => {
    set(activePinnedSidebarPanelAtom, value, { params });
    return value;
  },
});

export const propertySectionExpandedAtom = atom({
  path: ['transient', 'sidebar', 'propertySection', ':section'],
  defaultValue: true,
});

export const dataSourceSectionExpandedAtom = atom({
  path: ['transient', 'sidebar', 'dataSourceSection', ':section'],
  defaultValue: false,
});

export const componentSectionExpandedAtom = atom({
  path: ['transient', 'sidebar', 'componentSection', ':section'],
  defaultValue: true,
});

export const itemFilteredPageLevelAtom = atom({
  path: ['transient', ':treename', 'filteredOut', ':appUid', ':pageId'],
  defaultValue: {},
});

export const matchedItemsAtom = atom<string[]>({
  path: ['transient', ':treename', 'matchedItems', ':appUid', ':pageId'],
  defaultValue: [],
});

export const matchedItemsCurrentIndexAtom = atom({
  path: [
    'transient',
    ':treename',
    'matchedItemsCurrentIndex',
    ':appUid',
    ':pageId',
  ],
  defaultValue: -1,
});

export const itemFilteredAtom = atom({
  path: [...itemFilteredPageLevelAtom.path, ':itemId'],
  defaultValue: false,
});

export const itemExpandedPageLevelAtom = atom<Record<string, boolean>>({
  path: ['transient', ':treename', 'expanded', ':appUid', ':pageId'],
  defaultValue: {},
  urlParam: true,
});

// use itemExpandedSelector
export const itemExpandedAtom = atom({
  path: [...itemExpandedPageLevelAtom.path, ':itemId'],
  defaultValue: false,
});

export const sidebarUsersFilterAtom = atom({
  path: ['transient', 'sidebar', 'users', 'filter'],
  defaultValue: '',
});

export const sidebarUsersAddApplicationFilterAtom = atom({
  path: ['transient', 'sidebar', 'usersAddApp', 'filter'],
  defaultValue: '',
});

export const sidebarUsersFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'users', 'filterSize'],
  defaultValue: 0,
});

export const sidebarWorkflowsFilterAtom = atom({
  path: ['transient', 'sidebar', 'workflows', 'filter'],
  defaultValue: '',
});

export const workflowDesignAtom = atom({
  path: ['transient', 'sidebar', 'workflows', 'active'],
  defaultValue: '',
});
export const agenticWorkflowDesignAtom = atom({
  path: ['transient', 'sidebar', 'workflows', 'agenticActive'],
  defaultValue: '',
});

export const workflowDesignVersionAtom = atom({
  path: ['transient', 'sidebar', 'workflows', 'activeVersion'],
  defaultValue: -1,
});

export const workflowInstanceViewerAtom = atom({
  path: ['transient', 'sidebar', 'workflowInstance', 'active'],
  defaultValue: '',
});

export const sidebarRolesFilterAtom = atom({
  path: ['transient', 'sidebar', 'roles', 'filter'],
  defaultValue: '',
});

export const sidebarRolesAccessTypeAtom = atom({
  path: ['transient', 'sidebar', 'roles', 'access'],
  defaultValue: 'Users',
});

export const sidebarUserDashbordRolesAccessTypeAtom = atom({
  path: ['transient', 'sidebar', 'userDashboard', 'roles', 'access'],
  defaultValue: 'Pages',
});

export const sidebarActiveRoleUidAtom = atom({
  path: ['transient', 'sidebar', 'roles', 'activeRoleUid'],
  defaultValue: { roleUid: '', roleName: '' },
});

export const sidebarRolesAssignedOnlyToggleAtom = atom({
  path: ['transient', 'sidebar', 'roles', 'assignedOnly'],
  defaultValue: false,
});

export const sidebarRolesFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'roles', 'filterSize'],
  defaultValue: 0,
});

export const sidebarPagesFilterAtom = atom({
  path: ['transient', 'sidebar', 'pages', 'filter'],
  defaultValue: '',
});

export const recordIdBeforeRefresh = atom<undefined | string>({
  path: ['transient', ':dsAlias', 'recordIdBeforeRefresh'],
  defaultValue: undefined,
});

export const dataGridSelectedPath = atom<{
  dragFromCell: { rowIndex: number; columnIndex: number };
  dragToCell: { rowIndex: number; columnIndex: number };
  dragging: boolean;
}>({
  path: ['transient', 'datagrid', 'selectedColumns', 'index'],
  defaultValue: {
    dragFromCell: { rowIndex: 1, columnIndex: 0 },
    dragToCell: { rowIndex: 1, columnIndex: 0 },
    dragging: false,
  },
});

export const dataGridCopyData = atom<{
  copyData: string | null;
  copyDragFrom: { rowIndex: number; columnIndex: number } | null;
  copyDragTo: { rowIndex: number; columnIndex: number } | null;
  eventType?: string | undefined;
}>({
  path: ['transient', 'datagrid', 'selectedColumns', 'copyData'],
  defaultValue: {
    copyData: '',
    copyDragFrom: { rowIndex: 1, columnIndex: 0 },
    copyDragTo: { rowIndex: 1, columnIndex: 0 },
    eventType: undefined,
  },
});

export const dataGridSelectedData = atom({
  path: ['transient', 'datagrid', 'selectedColumns', 'Data'],
  defaultValue: new Map<string, string | undefined | null | number>(),
});

export const sidebarPagesFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'pages', 'filterSize'],
  defaultValue: 0,
});

export const sidebarFunctionsTypeFilterAtom = atom<
  IoFunctions['functionType'] | ''
>({
  path: ['transient', 'sidebar', 'functions', 'typefilter'],
  defaultValue: '',
});

export const sidebarFunctionsFilterAtom = atom({
  path: ['transient', 'sidebar', 'functions', 'filter'],
  defaultValue: '',
});

export const sidebarFunctionsFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'functions', 'filterSize'],
  defaultValue: 0,
});

export type TestView = 'edit' | 'list' | 'history';

export const sidebarTestViewAtom = atom<FnView>({
  path: ['transient', 'sidebarView', 'test'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarTestsFilterAtom = atom({
  path: ['transient', 'sidebar', 'tests', 'filter'],
  defaultValue: '',
});

export const sidebarTestsFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'tests', 'filterSize'],
  defaultValue: 0,
});

export const sidebarWorkflowNodesFilterAtom = atom({
  path: ['transient', 'sidebar', 'WorkflowNodes', 'filter'],
  defaultValue: '',
});

export const sidebarWorkflowNodesFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'WorkflowNodes', 'filterSize'],
  defaultValue: 0,
});

export const sidebarActiveWorkflowNodeUidAtom = atom({
  path: ['transient', 'activeId', 'wfNode'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveWorkflowNodeAuditIdAtom = atom({
  path: ['transient', 'activeAuditId', 'wfNode'],
  defaultValue: '',
  urlParam: true,
});

export const workflowNodeHistorySelectionAtom = atom<
  [number | null, number | null]
>({
  path: ['transient', 'compareFunction'],
  urlParam: true,
  defaultValue: [null, null],
});

export const workflowNodeHistoryDiffOpenAtom = atom({
  path: ['transient', 'compareFunctionDiffOpen'],
  urlParam: true,
  defaultValue: false,
});

export const appControllerAuditIdAtom = atom({
  path: ['transient', 'activeAuditId', 'appController'],
  defaultValue: '',
  urlParam: true,
});

export const appControllerHistorySelectionAtom = atom<
  [number | null, number | null]
>({
  path: ['transient', 'appControllerCompareFunction'],
  urlParam: true,
  defaultValue: [null, null],
});

export const appControllerHistoryDiffOpenAtom = atom({
  path: ['transient', 'appControllerCompareFunctionDiffOpen'],
  urlParam: true,
  defaultValue: false,
});

export const sidebarDataSourceFilterAtom = atom({
  path: ['transient', 'sidebar', 'ds', 'filter'],
  defaultValue: '',
});

export const sidebarExtDataSourceFilterAtom = atom({
  path: ['transient', 'sidebar', 'extds', 'filter'],
  defaultValue: '',
});

export const sidebarDataSourceWizardTitleAtom = atom({
  path: ['transient', 'sidebar', 'ds', 'wizardTitle'],
  defaultValue: 'How do you want to create?',
  urlParam: true,
});
export const sidebarPageWizardTitleAtom = atom({
  path: ['transient', 'sidebar', 'page', 'wizardTitle'],
  defaultValue: '',
});

export const sidebarPageWizardStepAtom = atom<'ds' | 'template' | ''>({
  path: ['transient', 'sidebar', 'page', 'step'],
  defaultValue: '',
});
export const sidebarPageWizardDataSourceAtom = atom({
  path: ['transient', 'sidebar', 'page', 'ds'],
  defaultValue: '',
});

export const sidebarDataSourceFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'ds', 'filterSize'],
  defaultValue: 0,
});

export const sidebarExtDataSourceFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'extds', 'filterSize'],
  defaultValue: 0,
});

export const sidebarLookupFilterAtom = atom({
  path: ['transient', 'sidebar', 'lkp', 'filter'],
  defaultValue: '',
});

export const sidebarLookupFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'lkp', 'filterSize'],
  defaultValue: 0,
});

export const sidebarProfileFilterAtom = atom({
  path: ['transient', 'sidebar', 'profile', 'filter'],
  defaultValue: '',
});

export const userProfileFilterAtom = atom({
  path: ['transient', 'user', 'profile', 'filter'],
  defaultValue: '',
});

export const sidebarProfileFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'profile', 'filterSize'],
  defaultValue: 0,
});

export const userProfileFilterResultsSizeAtom = atom({
  path: ['transient', 'user', 'profile', 'filterSize'],
  defaultValue: 0,
});

export const sidebarDataSourceAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'ds-access', 'filter'],
  defaultValue: '',
});

export const sidebarUserAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'user-access', 'filter'],
  defaultValue: '',
});

export const sidebarRolesUserAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'roles-user-access', 'filter'],
  defaultValue: '',
});

export const sidebarRolesDataSourceAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'roles-ds-access', 'filter'],
  defaultValue: '',
});

export const sidebarPageAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'pg-access', 'filter'],
  defaultValue: '',
});

export const sidebarRolesPageAccessFilterAtom = atom({
  path: ['transient', 'sidebar', 'roles-pg-access', 'filter'],
  defaultValue: '',
});

export const sidebarAttributesFilterAtom = atom({
  path: ['transient', 'sidebar', 'attr', 'filter'],
  defaultValue: '',
});

export type WHO =
  | 'createdBy'
  | 'creationDate'
  | 'lastUpdateDate'
  | 'lastUpdatedBy';

export const WHOAttributes: WHO[] = [
  'createdBy',
  'creationDate',
  'lastUpdateDate',
  'lastUpdatedBy',
];

export function isWhoAttribute(attr: IoDatasourceAttributes): boolean {
  return WHOAttributes.includes(attr.attributeCode as WHO);
}

export const sidebarAttributesMissingWhoAtom = atom<WHO[]>({
  path: ['transient', 'sidebar', 'attr', 'missingWho'],
  defaultValue: [],
});

export const sidebarAttributesShowRequiredAtom = atom({
  path: ['transient', 'sidebar', 'attr', 'showReqFields'],
  defaultValue: false,
});

export const sidebarLookupValuesFilterAtom = atom({
  path: ['transient', 'sidebar', 'lkpVal', 'filter'],
  defaultValue: '',
});

export const sidebarProfileValuesFilterAtom = atom({
  path: ['transient', 'sidebar', 'profileVal', 'filter'],
  defaultValue: '',
});

export const userProfileValuesFilterAtom = atom({
  path: ['transient', 'user', 'profileVal', 'filter'],
  defaultValue: '',
});

export const sidebarPropsFilterAtom = atom({
  path: ['transient', 'sidebar', 'props', 'filter'],
  defaultValue: '',
});

export const sidebarComponentsFilterAtom = atom({
  path: ['transient', 'sidebar', 'components', 'filter'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarComponentsFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'components', 'filterSize'],
  defaultValue: 0,
  urlParam: true,
});

export const sidebarComponentStoreFilterAtom = atom({
  path: ['transient', 'sidebar', 'componentStore', 'filter'],
  defaultValue: '',
});

export const sidebarComponentStoreFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'community-components', 'filterSize'],
  defaultValue: 0,
});

export const sidebarComponentsCheckboxAtom = atom({
  path: [
    'transient',
    'sidebar',
    'components',
    'showOnlyAllowedChildComponents',
  ],
  defaultValue: false,
});

export const sidebarSettingsFilterAtom = atom({
  path: ['transient', 'sidebar', 'settings', 'filter'],
  defaultValue: '',
});

export const treeViewFilterAtom = atom({
  path: ['transient', ':treename', 'tree', 'filter'],
  defaultValue: '',
  urlParam: true,
});

export const treeViewFilterMatchCaseAtom = atom({
  path: ['transient', ':treename', 'tree', 'filterMatchCase'],
  defaultValue: false,
  urlParam: true,
});

export const treeViewFilterMatchWholeWordAtom = atom({
  path: ['transient', ':treename', 'tree', 'filterMatchWholeWord'],
  defaultValue: false,
  urlParam: true,
});

export const treeViewSearchAtom = atom({
  path: ['transient', ':treename', 'tree', 'search'],
  defaultValue: '',
  urlParam: true,
});

export type DSView =
  | 'access'
  | 'duplicate'
  | 'edit'
  | 'editChild'
  | 'history'
  | 'list'
  | 'listChildren'
  | 'next'
  | 'wizard';

export type ExtDSView = 'duplicate' | 'edit' | 'history' | 'list';

export const sidebarActiveDatasourceIdAtom = atom({
  path: ['transient', 'activeId', 'ds'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveExtDatasourceIdAtom = atom({
  path: ['transient', 'activeId', 'extds'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarDatasourceEditModeAtom = atom<
  'Table' | 'Scratch' | 'Edit' | 'File' | 'SQL'
>({
  path: ['transient', 'editMode', 'ds'],
  defaultValue: 'Edit',
  urlParam: true,
});

export const sidebarActiveDatasourceHistoryAtom = atom<{
  attributeCode: string;
  pkValue: string;
}>({
  path: ['transient', 'activeId', 'dsAuditId'],
  defaultValue: { attributeCode: '', pkValue: '' },
  urlParam: true,
});

export const sidebarActiveExtDatasourceHistoryAtom = atom<{
  attributeCode: string;
  pkValue: string;
}>({
  path: ['transient', 'activeId', 'extdsAuditId'],
  defaultValue: { attributeCode: '', pkValue: '' },
  urlParam: true,
});

export const sidebarActiveAttributeIdAtom = atom({
  path: ['transient', 'activeId', 'dsAttr'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActivePageIdAtom = atom({
  path: ['transient', 'activeId', 'page'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarDSViewAtom = atom<DSView>({
  path: ['transient', 'sidebarView', 'ds'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarExtDSViewAtom = atom<ExtDSView>({
  path: ['transient', 'sidebarView', 'extds'],
  defaultValue: 'list',
  urlParam: true,
});

export type DBScriptView = 'edit' | 'list';

export const sidebarDBScriptsViewAtom = atom<DBScriptView>({
  path: ['transient', 'sidebarView', 'db_script'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarDBScriptsFilterAtom = atom({
  path: ['transient', 'sidebar', 'db_scripts', 'filter'],
  defaultValue: '',
});

export const sidebarDBScriptsFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'db_scripts', 'filterSize'],
  defaultValue: 0,
});

export const sidebarActiveDBScriptUidAtom = atom({
  path: ['transient', 'activeId', 'db_script'],
  defaultValue: '',
  urlParam: true,
});

export type UIScriptView = 'edit' | 'list';

export const sidebarUIScriptsViewAtom = atom<DBScriptView>({
  path: ['transient', 'sidebarView', 'ui_script'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarUIScriptsFilterAtom = atom({
  path: ['transient', 'sidebar', 'ui_scripts', 'filter'],
  defaultValue: '',
});

export const sidebarUIScriptsFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'ui_scripts', 'filterSize'],
  defaultValue: 0,
});

export const sidebarActiveUIScriptUidAtom = atom({
  path: ['transient', 'activeId', 'ui_script'],
  defaultValue: '',
  urlParam: true,
});

export type FnView = 'edit' | 'list' | 'history' | 'runs';

export const sidebarFnViewAtom = atom<FnView>({
  path: ['transient', 'sidebarView', 'fn'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarFunctionsScheduledRunsFilterAtom = atom({
  path: ['transient', 'sidebar', 'fnSch', 'filter'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarFunctionsScheduledRunsStatusAtom =
  atom<FunctionsScheduledRunsStatus>({
    path: ['transient', 'sidebar', 'fnSchs', 'filter'],
    defaultValue: 'E',
    urlParam: true,
  });

export const sidebarScheduledFunctionRunsStatusAtom =
  atom<FunctionsScheduledRunsStatus>({
    path: ['transient', 'sidebar', 'fnSchsRuns', 'filter'],
    defaultValue: 'E',
    urlParam: true,
  });

export type WorkflowNodesView = FnView;

export const sidebarWorkflowNodesViewAtom = atom<WorkflowNodesView>({
  path: ['transient', 'sidebarView', 'wfnodes'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveFunctionUidAtom = atom({
  path: ['transient', 'activeId', 'fn'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveTestUidAtom = atom({
  path: ['transient', 'activeId', 'test'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveLookupUidAtom = atom({
  path: ['transient', 'activeId', 'lkp'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveLookupValueUidAtom = atom({
  path: ['transient', 'activeId', 'lkpValue'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveProfileUidAtom = atom({
  path: ['transient', 'activeId', 'profile'],
  defaultValue: '',
  urlParam: true,
});

export const userActiveProfileUidAtom = atom({
  path: ['transient', 'userActiveProfileId', 'profile'],
  defaultValue: '',
});

export const sidebarActiveProfileValueUidAtom = atom({
  path: ['transient', 'activeId', 'profileValue'],
  defaultValue: '',
  urlParam: true,
});

export const userActiveProfileValueUidAtom = atom({
  path: ['transient', 'userActiveProfileValueId', 'profileValue'],
  defaultValue: '',
});

export const sidebarActiveFunctionAuditIdAtom = atom({
  path: ['transient', 'activeAuditId', 'fn'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveTestAuditIdAtom = atom({
  path: ['transient', 'activeAuditId', 'test'],
  defaultValue: '',
  urlParam: true,
});

export const testHistorySelectionAtom = atom<[number | null, number | null]>({
  path: ['transient', 'compareTest'],
  urlParam: true,
  defaultValue: [null, null],
});

export const testHistoryDiffOpenAtom = atom({
  path: ['transient', 'compareTestDiffOpen'],
  urlParam: true,
  defaultValue: false,
});

export const functionHistorySelectionAtom = atom<
  [number | null, number | null]
>({
  path: ['transient', 'compareFunction'],
  urlParam: true,
  defaultValue: [null, null],
});

export const functionHistoryDiffOpenAtom = atom({
  path: ['transient', 'compareFunctionDiffOpen'],
  urlParam: true,
  defaultValue: false,
});

export const pageHistoryDiffOpenAtom = atom({
  path: ['transient', 'comparePageDiffOpen'],
  urlParam: true,
  defaultValue: false,
});

export type LookupView = 'edit' | 'editChild' | 'list' | 'listChildren';

export const sidebarLookupViewAtom = atom<LookupView>({
  path: ['transient', 'sidebarView', 'lkp'],
  defaultValue: 'list',
  urlParam: true,
});

export type ProfileView = 'edit' | 'editChild' | 'list' | 'listChildren';

export const sidebarProfileViewAtom = atom<ProfileView>({
  path: ['transient', 'sidebarView', 'profile'],
  defaultValue: 'list',
  urlParam: true,
});

export const userProfileViewAtom = atom<ProfileView>({
  path: ['transient', 'userProfileView', 'profile'],
  defaultValue: 'list',
});

export type ComponentView = 'list' | 'help';

export const sidebarComponentViewAtom = atom<ComponentView>({
  path: ['transient', 'sidebarView', 'comp'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveComponentAtom = atom({
  path: ['transient', 'sidebarView', 'activeComp'],
  defaultValue: 'Page',
  urlParam: true,
});

// Components Playground dialog open state
export const componentsPlaygroundOpenAtom = atom({
  path: ['transient', 'sidebar', 'components', 'playgroundOpen'],
  defaultValue: false,
});

export type PageView =
  | 'access'
  | 'controllerHistory'
  | 'edit'
  | 'history'
  | 'list'
  | 'next'
  | 'wizard';

export const sidebarPageViewAtom = atom<PageView>({
  path: ['transient', 'sidebarView', 'page'],
  defaultValue: 'list',
  urlParam: true,
});

export type AppSettingsView =
  | 'home'
  | 'appController'
  | 'appControllerHistory'
  | 'history';

export const appSettingsViewAtom = atom<AppSettingsView>({
  path: ['transient', 'sidebarView', 'appSettings', ':category'],
  defaultValue: 'home',
  urlParam: true,
});

export type RolesView = 'edit' | 'list' | 'ds' | 'pages' | 'users' | 'access';

export const sidebarRolesViewAtom = atom<RolesView>({
  path: ['transient', 'sidebarView', 'roles'],
  defaultValue: 'list',
});

export type UsersView = 'edit' | 'list' | 'access' | 'appAccess';

export const sidebarUsersViewAtom = atom<UsersView>({
  path: ['transient', 'sidebarView', 'users'],
  defaultValue: 'list',
});

// Workflow Tab

export const sidebarWorkflowInstanceFilterAtom = atom({
  path: ['transient', 'sidebar', 'wfi', 'filter'],
  defaultValue: '',
  urlParam: true,
});

export type WorkflowInstanceSource = 'Active' | 'Completed';

export const sidebarWorkflowInstanceSourceAtom = atom<WorkflowInstanceSource>({
  path: ['transient', 'sidebar', 'wfis', 'filter'],
  defaultValue: 'Active',
  urlParam: true,
});

export type WFView = 'edit' | 'list' | 'instances';

export const sidebarWFViewAtom = atom<WFView>({
  path: ['transient', 'sidebarView', 'wf'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveWorkflowIdAtom = atom({
  path: ['transient', 'activeId', 'wf'],
  defaultValue: '',
  urlParam: true,
});

// Agent wf Tab

export const sidebarWorkflowInstanceAgentFilterAtom = atom({
  path: ['transient', 'sidebar', 'agentic-wfi', 'filter'],
  defaultValue: '',
  urlParam: true,
});

export type WorkflowInstanceAgentSource = 'Active' | 'Completed';

export const sidebarWorkflowInstanceAgentSourceAtom =
  atom<WorkflowInstanceSource>({
    path: ['transient', 'sidebar', 'agentic-wfis', 'filter'],
    defaultValue: 'Active',
    urlParam: true,
  });

export type WFAgentView = 'edit' | 'list' | 'instances';

export const sidebarWFAgentViewAtom = atom<WFAgentView>({
  path: ['transient', 'sidebarView', 'agentic-wf'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveAgentWorkflowIdAtom = atom({
  path: ['transient', 'activeId', 'agentic-wf'],
  defaultValue: '',
  urlParam: true,
});

// Connections Tab

export type ConnectionView = 'edit' | 'list';

export const sidebarConnectionViewAtom = atom<ConnectionView>({
  path: ['transient', 'sidebarView', 'connection'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveConnectionUidAtom = atom({
  path: ['transient', 'activeId', 'connection'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarConnectionFilterAtom = atom({
  path: ['transient', 'sidebar', 'Connection', 'filter'],
  defaultValue: '',
});

export const sidebarConnectionFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'Connection', 'filterSize'],
  defaultValue: 0,
});

// Agent Group Tab

export type AgentGroupView = 'edit' | 'list';

export const sidebarAgentGroupViewAtom = atom<AgentGroupView>({
  path: ['transient', 'sidebarView', 'agentGroup'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveAgentGroupUidAtom = atom({
  path: ['transient', 'activeId', 'agentGroup'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarAgentGroupFilterAtom = atom({
  path: ['transient', 'sidebar', 'AgentGroup', 'filter'],
  defaultValue: '',
});

export const sidebarAgentGroupFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'AgentGroup', 'filterSize'],
  defaultValue: 0,
});

// Email Requests Tab

export type EmailRequestsView = 'details' | 'list' | 'fnDetails';
export type EmailRequestsSource = 'PENDING' | 'COMPLETED' | 'ERROR';

export const sidebarActiveRequestUidAtom = atom({
  path: ['transient', 'activeId', 'emailRequests'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarEmailRequestsFilterAtom = atom({
  path: ['transient', 'sidebar', 'emailRequests', 'filter'],
  defaultValue: '',
});

export const sidebarEmailRequestsSourceAtom = atom<EmailRequestsSource>({
  path: ['transient', 'sidebar', 'emailRequests', 'source'],
  defaultValue: 'PENDING',
  urlParam: true,
});

export const sidebarSelectedEmailRequestAtom = atom<string[]>({
  path: ['transient', 'sidebar', 'emailRequests', 'resend'],
  defaultValue: [],
});

export const sidebarSelectedAttributesListAtom = atom<string[]>({
  path: ['transient', 'sidebar', ':ds', 'selectedAttributeList'],
  defaultValue: [],
});

export const sidebarEmailRequestStatusFilterAtom = atom<string>({
  path: ['transient', 'sidebar', 'emailRequests', 'statusFilter'],
  defaultValue: '',
});

// Languages Tab

export type LanguageView = 'edit' | 'list';

export const sidebarLanguageViewAtom = atom<LanguageView>({
  path: ['transient', 'sidebarView', 'connection'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveLanguageUidAtom = atom({
  path: ['transient', 'activeId', 'connection'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarLanguageFilterAtom = atom({
  path: ['transient', 'sidebar', 'Language', 'filter'],
  defaultValue: '',
});

export const sidebarLanguageFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'Language', 'filterSize'],
  defaultValue: 0,
});

// Image Tab

export type ImageView = 'edit' | 'list';

export const sidebarImageViewAtom = atom<ImageView>({
  path: ['transient', 'sidebarView', 'connection'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveImageUidAtom = atom({
  path: ['transient', 'activeId', 'Image'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveImageVersionAtom = atom({
  path: ['transient', 'activeVersion', 'Image'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarImageFilterAtom = atom({
  path: ['transient', 'sidebar', 'Image', 'filter'],
  defaultValue: '',
});

export const sidebarImageFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'Image', 'filterSize'],
  defaultValue: 0,
});

// Storage Tab

export const sidebarStorageConnectionViewAtom = atom<ConnectionView>({
  path: ['transient', 'sidebarView', 'storageConnection'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveStorageConnectionUidAtom = atom({
  path: ['transient', 'activeId', 'storageConnection'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarStorageConnectionFilterAtom = atom({
  path: ['transient', 'sidebar', 'storageConnection', 'filter'],
  defaultValue: '',
});

export const sidebarStorageConnectionFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'storageConnection', 'filterSize'],
  defaultValue: 0,
});

// Applications Tab

export type ApplicationView = 'edit' | 'list' | 'add-access';

export const sidebarApplicationViewAtom = atom<ApplicationView>({
  path: ['transient', 'sidebarView', 'application'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActiveApplicationUidAtom = atom({
  path: ['transient', 'activeId', 'application'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarApplicationFilterAtom = atom({
  path: ['transient', 'sidebar', 'Application', 'filter'],
  defaultValue: '',
});

export const sidebarApplicationFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'Application', 'filterSize'],
  defaultValue: 0,
});

// Patch Tab

export type PatchView = 'edit' | 'list' | 'select' | 'upload';

export const sidebarPatchViewAtom = atom<PatchView>({
  path: ['transient', 'sidebarView', 'patch'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarActivePatchUidAtom = atom({
  path: ['transient', 'activeId', 'patch'],
  defaultValue: -1,
  urlParam: true,
});

export const sidebarPatchFilterAtom = atom({
  path: ['transient', 'sidebar', 'Patch', 'filter'],
  defaultValue: '',
});

export const sidebarPatchFilterResultsSizeAtom = atom({
  path: ['transient', 'sidebar', 'Patch', 'filterSize'],
  defaultValue: 0,
});

export const liveStatusSignal = observable<Record<string, LiveStatus>>({});

export const scheduleUpdateSignal = observable<Record<string, ScheduleUpdate>>(
  {},
);

export type ScheduledRunsSource = 'Success' | 'Error';

export const sidebarScheduledRunsFilterAtom = atom({
  path: ['transient', 'sidebar', 'schRuns', 'filter'],
  defaultValue: '',
});

export const sidebarScheduledRunsViewAtom = atom<EmailRequestsView>({
  path: ['transient', 'sidebarView', 'schRuns'],
  defaultValue: 'list',
  urlParam: true,
});

export const sidebarScheduledRunsFunsAtom = atom<boolean>({
  path: ['transient', 'sidebarView', 'expandFuns'],
  defaultValue: false,
  urlParam: true,
});

export const sidebarScheduledRunsWrkFlwsAtom = atom<boolean>({
  path: ['transient', 'sidebarView', 'expandWrkFlws'],
  defaultValue: false,
  urlParam: true,
});

export const sidebarActiveJobAtom = atom({
  path: ['transient', 'activeId', 'schRuns'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarActiveJobNameAtom = atom({
  path: ['transient', 'activeName', 'schRuns'],
  defaultValue: '',
  urlParam: true,
});

export const sidebarScheduledRunsSourceAtom = atom({
  path: ['transient', 'sidebar', 'schRuns', 'source'],
  defaultValue: 'Success',
  urlParam: true,
});

export const sidebarSelectedAsyncExports = atom<string[]>({
  path: ['transient', 'sidebar', 'asyncExports'],
  defaultValue: [],
});

export const adminDashboardPagesFetchedAtom = atom<boolean>({
  path: ['transient', 'adminDashboard', 'pages', 'fetched'],
  defaultValue: false,
});

export const adminDashboardDsFetchedAtom = atom<boolean>({
  path: ['transient', 'adminDashboard', 'ds', 'fetched'],
  defaultValue: false,
});

export const adminDashboardActiveRoleAppAtom = atom<string>({
  path: ['transient', 'adminDashboard', 'roleActivApp'],
  defaultValue: '',
});

export const adminDashboardActiveUserInfoAtom = atom({
  path: ['transient', 'admiDashboard', 'activeUserInfo'],
  defaultValue: { userName: '', displayName: '' },
});

export const adminDashboardActiveAppInfoAtom = atom({
  path: ['transient', 'admiDashboard', 'activeAppInfo'],
  defaultValue: { appUid: '', appName: '' },
});

export const adminDashboardActiveRoleInfoAtom = atom({
  path: ['transient', 'admiDashboard', 'activeRole'],
  defaultValue: { roleUid: '', roleName: '' },
});

export const applicationDashboardActiveRoleInfoAtom = atom({
  path: ['transient', 'applicationDashboard', 'activeRoleInfo'],
  defaultValue: { roleUid: '', roleName: '' },
});

export const applicationDashboardActiveAppInfoAtom = atom({
  path: ['transient', 'applicationDashboard', 'activeAppInfo'],
  defaultValue: { appUid: '', appName: '' },
});
